import React, { useEffect, useMemo, useRef } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, CircularProgress, Stack } from '@mui/material';
import { ChainOfThought } from './ChainOfThought';
import type { CardState, TimelineNode } from './interfaces';
import {
  isCancelledOrPending,
  nodeDescription,
  nodeLabel,
  nodeToStatus,
  summarizeInput,
  type TimelineStepStatus,
} from './Timeline.utils';
import {
  attachReasoningToToolCalls,
  flattenTimelineTree,
  flattenNodes,
  getTimelineNodeKey,
  groupParallelNodes,
  collapseRepeatedToolCalls,
} from './utils.timeline';

const CompletedCheckIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <circle cx={12} cy={12} r={10} stroke="currentColor" strokeWidth={2} fill="none" />
    <path
      d="M8 12.5l2.5 2.5L16 9.5"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

function stepIcon(node: TimelineNode, status: TimelineStepStatus): React.ReactNode {
  if (node.kind === 'error') return <ErrorOutlineIcon sx={{ fontSize: 18 }} />;

  if (status === 'complete') return <CompletedCheckIcon />;
  return <CircularProgress size={16} thickness={5} sx={{ color: 'text.secondary' }} />;
}

function TimelineStep({
  node,
  isLast,
  nestingLevel = 0,
}: {
  node: TimelineNode;
  isLast: boolean;
  nestingLevel?: number;
}) {
  if (node.kind === 'grouped_tool_calls') {
    const status = nodeToStatus(node);
    const label = nodeLabel(node);
    const icon = stepIcon(node, status);
    return <ChainOfThought.Step icon={icon} label={label} status={status} isLast={isLast} />;
  }

  if (node.kind === 'parallel_group') {
    return (
      <ChainOfThought.ParallelGroup>
        {node.children.map((child, i) => (
          <TimelineStep
            key={getTimelineNodeKey(child)}
            node={child}
            nestingLevel={nestingLevel}
            isLast={isLast && i === node.children.length - 1}
          />
        ))}
      </ChainOfThought.ParallelGroup>
    );
  }

  const status = nodeToStatus(node);
  const label = nodeLabel(node);
  const description = nodeDescription(node);
  const icon = stepIcon(node, status);

  const reasoningText = node.kind === 'tool_call' ? node.reasoningText : undefined;
  const inputFallback = node.kind === 'tool_call' ? summarizeInput(node.block.input) : undefined;
  const nestedChildren =
    node.kind === 'task' && nestingLevel === 0
      ? groupParallelNodes(
          attachReasoningToToolCalls(
            node.children.filter((child) => {
              // Hide child task nodes whose label duplicates the parent's label
              if (child.kind === 'task') {
                const childLabel = nodeLabel(child);
                return childLabel !== label;
              }
              return true;
            })
          )
        )
      : [];

  return (
    <ChainOfThought.Step icon={icon} label={label} description={description} status={status} isLast={isLast}>
      {reasoningText ? (
        <ChainOfThought.CollapsibleDescription text={reasoningText} fallback={inputFallback} />
      ) : null}
      {nestedChildren.length > 0 ? (
        <Box sx={{ mt: 0.5, pl: 1.5, borderLeft: 1, borderColor: 'divider' }}>
          <Stack spacing={0.75}>
            {nestedChildren.map((child, i) => (
              <TimelineStep
                key={getTimelineNodeKey(child)}
                node={child}
                nestingLevel={nestingLevel + 1}
                isLast={i === nestedChildren.length - 1}
              />
            ))}
          </Stack>
        </Box>
      ) : null}
    </ChainOfThought.Step>
  );
}

function TimelineStepList({ nodes, isCancelled }: { nodes: TimelineNode[]; isCancelled: boolean }) {
  if (!isCancelled) {
    return (
      <>
        {nodes.map((node, i) => (
          <TimelineStep key={getTimelineNodeKey(node)} node={node} isLast={i === nodes.length - 1} />
        ))}
      </>
    );
  }

  // Split into before/after cancellation point
  const lastNonCancelledIdx = nodes.reduce((last, node, i) => (isCancelledOrPending(node) ? last : i), -1);

  const beforeCancellation = nodes.slice(0, lastNonCancelledIdx + 1);
  const afterCancellation = nodes.slice(lastNonCancelledIdx + 1);

  return (
    <>
      {beforeCancellation.map((node, i) => (
        <TimelineStep
          key={getTimelineNodeKey(node)}
          node={node}
          isLast={afterCancellation.length === 0 && i === beforeCancellation.length - 1}
        />
      ))}
      {afterCancellation.length > 0 ? (
        <>
          <ChainOfThought.CancellationDivider />
          {afterCancellation.map((node, i) => (
            <TimelineStep
              key={getTimelineNodeKey(node)}
              node={node}
              isLast={i === afterCancellation.length - 1}
            />
          ))}
        </>
      ) : (
        <ChainOfThought.CancellationDivider />
      )}
    </>
  );
}

export interface AgentProgressTimelineProps {
  className?: string;
  nodes: TimelineNode[];
  cardState?: CardState;
  defaultOpen?: boolean;
}

export function AgentProgressTimeline({
  className,
  nodes,
  cardState,
  defaultOpen,
}: AgentProgressTimelineProps) {
  const isTerminal = cardState?.phase === 'complete' || cardState?.phase === 'cancelled';

  // Keep scope nodes transparent in the UI while preserving task-level nesting.
  const displayNodes = useMemo(() => flattenNodes(nodes), [nodes]);
  const allNodes = useMemo(() => flattenTimelineTree(displayNodes), [displayNodes]);

  // True when the timeline contains at least one non-reasoning node
  // (tool call, scope, task, etc.) that gives the user something to see.
  const hasContentNodes = useMemo(() => allNodes.some((n) => n.kind !== 'reasoning'), [allNodes]);

  const [open, setOpen] = React.useState(defaultOpen ?? (!isTerminal && hasContentNodes));
  const prevPhaseRef = useRef(cardState?.phase);
  const prevHasContentRef = useRef(hasContentNodes);

  useTimelineAutoExpand(hasContentNodes, isTerminal, setOpen, prevHasContentRef);
  useTimelineAutoCollapse(cardState?.phase, isTerminal, setOpen, prevPhaseRef);

  if (nodes.length === 0) {
    return null;
  }

  const withReasoning = attachReasoningToToolCalls(displayNodes);
  const grouped = groupParallelNodes(withReasoning);
  const collapsed = collapseRepeatedToolCalls(grouped);

  return (
    <TimelineRenderer
      className={className}
      open={open}
      onOpenChange={setOpen}
      cardState={cardState}
      grouped={collapsed}
    />
  );
}

function TimelineRenderer({
  className,
  open,
  onOpenChange,
  cardState,
  grouped,
}: {
  className?: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  cardState?: CardState;
  grouped: TimelineNode[];
}) {
  const isCancelled = cardState?.phase === 'cancelled';

  return (
    <ChainOfThought.Root open={open} onOpenChange={onOpenChange} className={className}>
      <ChainOfThought.Header
        phase={cardState?.phase ?? 'thinking'}
        subtitle={cardState?.headerSubtitle}
        completedSteps={cardState?.completedSteps}
        totalSteps={cardState?.totalSteps}
      >
        {cardState?.headerLabel}
      </ChainOfThought.Header>
      <ChainOfThought.Content>
        <TimelineStepList nodes={grouped} isCancelled={isCancelled} />
        {cardState?.summaryMetadata && cardState.phase === 'complete' ? (
          <ChainOfThought.StepResult>{cardState.summaryMetadata}</ChainOfThought.StepResult>
        ) : null}
      </ChainOfThought.Content>
    </ChainOfThought.Root>
  );
}

function useTimelineAutoExpand(
  hasContentNodes: boolean,
  isTerminal: boolean,
  setOpen: (v: boolean) => void,
  prevHasContentRef: React.MutableRefObject<boolean>
) {
  useEffect(() => {
    if (hasContentNodes && !prevHasContentRef.current && !isTerminal) {
      setOpen(true);
    }
    prevHasContentRef.current = hasContentNodes;
  }, [hasContentNodes, isTerminal, setOpen, prevHasContentRef]);
}

function useTimelineAutoCollapse(
  phase: CardState['phase'] | undefined,
  isTerminal: boolean,
  setOpen: (v: boolean) => void,
  prevPhaseRef: React.MutableRefObject<CardState['phase'] | undefined>
) {
  useEffect(() => {
    const wasTerminal = prevPhaseRef.current === 'complete' || prevPhaseRef.current === 'cancelled';
    if (isTerminal && !wasTerminal && prevPhaseRef.current !== undefined) {
      setOpen(false);
    }
    prevPhaseRef.current = phase;
  }, [phase, isTerminal, setOpen, prevPhaseRef]);
}
